@if (showSpinner) {
  <div class="centered-spinner-holder">
    <cs-spinner></cs-spinner>
  </div>
}
@if (!loading) {
  <cs-card-list [class.animate-in]="animateContent">
    <ng-content select="cs-card-list-row"></ng-content>
    <!-- The coach's billing card (#4393 Surface B merged with #4455).
         ONE card, saying up to two things:

         1. the money problem, in one of three wordings. Only the Paddle rail
            may name a failed payment — the app stores map a deliberate cancel
            onto the same status, so their wording has to stay neutral or it
            would be a lie for some coaches.
         2. what it means for the coach's clients, added ONLY when they really
            do lose access and there is at least one of them. An admin-granted
            Legacy coach can have a failing tier whose clients keep access
            regardless, and this card must not tell them otherwise.

         The card itself never depends on the client count: a coach with none
         still gets it, because seat #1 of a Paddle coaching subscription pays
         for the coach's OWN membership. See plans/coach-billing-notifications.md
         and design-docs/coach-subscription-lapse.md. -->
    <!-- "We've updated our Terms and Privacy Policy" (#5587). Only drawn during the gentle
         month: after that the consent screen raises itself over everything, so
         there is nothing left for a card to ask. Dismissing hides it until the
         app is restarted, never for good — the deadline is what ends the asking. -->
    @if (showsNotice(Notice.PrivacyUpdate)) {
      <cs-info-card id="day-items-privacy-update-card" type="info" [dismissible]="true"
        [message]="LL.consent.user.updatedCard()"
        [actionText]="LL.consent.user.updatedCardAction()"
        (actionClick)="notices.privacyUpdate.onActionClick()"
        (dismissed)="notices.privacyUpdate.onDismissed()">
      </cs-info-card>
    }
    @if (showsNotice(Notice.CoachBilling)) {
      <cs-info-card id="day-items-coach-billing-card" type="warning" [dismissible]="true"
        (dismissed)="notices.coachBilling.onDismissed()">
        @switch (notices.coachBilling.kind) {
          @case ('payment-failed') {
            <strong>{{ LL.dayItems.warnings.coachPaymentFailedTitle() }}</strong><br>
            @if (notices.coachBilling.endDate) {
              {{ LL.dayItems.warnings.coachPaymentFailed({ date: notices.coachBilling.endDate }) }}
            } @else {
              {{ LL.dayItems.warnings.coachPaymentFailedNoDate() }}
            }
          }
          @case ('not-renewing') {
            <strong>{{ LL.dayItems.warnings.coachNotRenewingTitle() }}</strong><br>
            @if (notices.coachBilling.endDate) {
              {{ LL.dayItems.warnings.coachNotRenewing({ date: notices.coachBilling.endDate }) }}
            } @else {
              {{ LL.dayItems.warnings.coachNotRenewingNoDate() }}
            }
          }
          @case ('stopped') {
            <strong>{{ LL.dayItems.warnings.coachPlanStoppedTitle() }}</strong><br>
            {{ LL.dayItems.warnings.coachPlanStopped() }}
          }
        }
        <!-- Zero means "say nothing about clients" — see the decision rule. The
             tense differs because a stopped plan has ALREADY cut them off,
             while the other two wordings are about a future date. -->
        @if (notices.coachBilling.clientCount > 0) {
          <br>
          @if (notices.coachBilling.kind === 'stopped') {
            {{ LL.dayItems.warnings.coachClientsLostAccess({ count: notices.coachBilling.clientCount }) }}
          } @else {
            {{ LL.dayItems.warnings.coachClientsWillLoseAccess({ count: notices.coachBilling.clientCount }) }}
          }
        }
        <br>
        <!-- Only a Paddle per-seat coach can fix this inside the app. An
             app-store fixed-tier coach has to do it in their store, so they get
             told that instead of a button that cannot help them. -->
        @if (notices.coachBilling.canManageInApp) {
          <button class="link" (click)="notices.coachBilling.onActionClick()">{{ LL.dayItems.warnings.coachLapseManageInApp() }}</button>
        } @else {
          {{ LL.dayItems.warnings.coachLapseManageInStore() }}
        }
      </cs-info-card>
    }
    <!-- The MEMBER's own billing card (df-6f0a2f8274a3).
         Shown while a web-billed membership's renewal payment is being retried
         and the person still has access — the only window in which they can fix
         the card and keep their membership. Until this existed, the app said so
         only on the User Profile page, so somebody could open the app every day
         and find out nothing until their access had already gone.

         It can never describe the SAME failed payment as the coach card above,
         because this one needs a `Paddle` licence type and so excludes anyone
         whose own membership is paid by a coaching subscription. The two are
         not mutually exclusive in general though: the coach card fires on the
         mere presence of a coach block, so somebody carrying a residual one
         while paying for their own Paddle membership can see both — two
         different subscriptions, not two copies of one message. See
         cs-day-items-member-billing-card.ts.

         The action is an IN-APP navigation to the membership page, never a link
         out to Paddle's portal — see the handler for why that line matters. -->
    @if (showsNotice(Notice.MemberBilling)) {
      <cs-info-card id="day-items-member-billing-card" type="warning" [dismissible]="true"
        (dismissed)="notices.memberBilling.onDismissed()">
        <strong>{{ LL.dayItems.warnings.memberPaymentFailedTitle() }}</strong><br>
        @if (notices.memberBilling.endDate) {
          {{ LL.dayItems.warnings.memberPaymentFailed({ date: notices.memberBilling.endDate }) }}
        } @else {
          {{ LL.dayItems.warnings.memberPaymentFailedNoDate() }}
        }
        <br>
        <button class="link" (click)="notices.memberBilling.onActionClick()">{{ LL.dayItems.warnings.memberPaymentFailedViewMembership() }}</button>
      </cs-info-card>
    }
    <!-- Weight warning card -->
    @if (showsNotice(Notice.WeightWarning)) {
      <cs-info-card type="warning">
        {{ weightWarningMessage }}
        <button class="link" (click)="onLogWeightClick($event)">{{ LL.dayItems.warnings.logWeightLink() }}</button>
      </cs-info-card>
    }
    <!-- Plan update warning card -->
    @if (showsNotice(Notice.PlanUpdate)) {
      <cs-info-card type="info">
        {{ LL.dayItems.warnings.planUpdateNeeded() }}
        <button class="link" (click)="onUpdateProgramClick($event)">{{ LL.dayItems.warnings.updateProgramLink() }}</button>
      </cs-info-card>
    }
    <!-- Pending workout warning card -->
    @if (showsNotice(Notice.PendingWorkout)) {
      <cs-info-card type="warning" [dismissible]="true"
        (dismissed)="onPendingWorkoutWarningDismissed()">
        {{ LL.dayItems.warnings.pendingWorkout() }} <button class="link" (click)="navigateToPendingWorkoutDay()">{{
      pendingWorkoutDateLabel }}</button>.
    </cs-info-card>
  }
    <!-- "Update available" store-side prompt vs legacy "What's new"
         changelog nudge — mutually exclusive in the same dashboard
         slot. PRD §3: when the user is behind the store, showing the
         "here's what changed in your current version" nudge while a
         newer build exists is confusing, so the update-available
         card supersedes. That exclusion is NOT in these two blocks —
         it lives with every other notice-card rule in
         cs-day-items-notice-stack.ts, so showsNotice(Notice.WhatsNew)
         already answers false while the update prompt is drawn. -->
    @if (showsNotice(Notice.UpdateAvailable)) {
      <!-- message + action switch by the live install state (Android
           flexible flow: available → downloading → restart). While
           downloading, actionText() returns undefined so the card shows
           no action button. The card is only dismissible in the
           available mode — dismissing mid-download/ready would strand
           the in-progress update. These three are called from HERE on
           purpose: each reads the install-state signal during template
           evaluation, which is what auto-tracks it for zoneless change
           detection. -->
      <cs-info-card id="day-items-update-available-card" type="info"
        [message]="notices.updateAvailable.message()"
        [actionText]="notices.updateAvailable.actionText()"
        [dismissible]="notices.updateAvailable.isDismissible()"
        (actionClick)="notices.updateAvailable.onActionAsync()"
        (dismissed)="notices.updateAvailable.onDismissedAsync()">
      </cs-info-card>
    }
    @if (showsNotice(Notice.WhatsNew)) {
      <cs-info-card id="day-items-whats-new-card" type="info" [dismissible]="true"
        (dismissed)="notices.whatsNew.onDismissedAsync()">
        {{ LL.app.updated({ version: notices.whatsNew.version }) }} <button class="link" (click)="notices.whatsNew.onOpenClick()">{{ LL.app.whatsNew() }}</button>
      </cs-info-card>
    }
    <!-- Unfinished weekly checkup warning card -->
    @if (showsNotice(Notice.UnfinishedWeeklyCheckup)) {
      <cs-info-card type="warning" [dismissible]="true"
        (dismissed)="onUnfinishedWeeklyCheckupWarningDismissed()">
        {{ LL.dayItems.warnings.unfinishedWeeklyCheckup() }} <button class="link" (click)="navigateToUnfinishedWeeklyCheckupDay()">{{
      unfinishedWeeklyCheckupDateLabel }}</button>.
    </cs-info-card>
  }
    <!-- First workout reminder for new users on rest day -->
    @if (showsNotice(Notice.FirstWorkoutReminder)) {
      <cs-info-card type="info" [dismissible]="true"
        (dismissed)="firstWorkoutReminderDismissed = true">
        {{ LL.dayItems.warnings.firstWorkoutReminder({ day: firstWorkoutDayName }) }}
      </cs-info-card>
    }
  @for (item of schedule; track item.id) {
    <cs-card hoverable id="{{item.id}}" [class.card--done]="isItemDone(item.entry.state)"
      [class.card--incomplete]="isItemIncomplete(item.entry.state)"
      [class.card--skipped]="isItemSkipped(item.entry.state)" [highlighted]="item.highlighted"
      (click)="clickedItem($event, item)" tappable>
      <cs-card-title>
        <div class="card-header__left" title-content>
          <div class="item-time">
            {{ item.time }}
          </div>
          <div class="item-title-row">
            <span class="item-title" [class.item-title--strikethrough]="item.entry.state === LogRecordState.Skipped">{{
              item.title
            }}</span>
            @if (hasAdditionalActions(item)) {
              <cs-icon-button icon="ellipsis-vertical"
              (click)="showActionMenu($event, item)"></cs-icon-button>
            }
          </div>
        </div>
        <div class="card-header__right" title-action>
          <!-- Train Today button for rest day card -->
          @if (item.id === 'NoWorkout' && atToday) {
            <cs-button [label]="LL.training.trainToday()" secondary x-small
              (click)="handleStartWorkoutClick(); $event.stopPropagation()">
            </cs-button>
          }
          <!-- Show button for certain states -->
          @if (shouldShowDoneButton(item)) {
            <cs-button [label]="getDoneButtonText(item)" secondary x-small
              (click)="handlePrimaryButtonClick(item); $event.stopPropagation()">
            </cs-button>
          }
          <!-- Show status indicator for completed/skipped/in-progress states -->
          @if (shouldShowStatusIndicator(item)) {
            <div class="status-indicator">
              @if (item.entry.state === LogRecordState.Done) {
                <cs-status-icon [tone]="StatusIconTone.Done"></cs-status-icon>
              }
              @if (item.entry.state === LogRecordState.Incomplete && item.entry.type === LogRecordType.Workout) {
                <cs-status-icon [tone]="StatusIconTone.InProgress" icon="time"></cs-status-icon>
              }
              @if (item.entry.state === LogRecordState.Skipped) {
                <span class="status-text status-text--skipped">{{ LL.common.states.skipped() }}</span>
              }
            </div>
          }
        </div>
      </cs-card-title>
      @if (item.entry.type !== LogRecordType.DayStart && item.entry.type !== LogRecordType.DayEnd) {
        <cs-card-content>
          <!-- For meals, show inline-compact or linear nutrient list based on context -->
          @if (item.entry.type === LogRecordType.Meal && item.nutrients) {
            <div class="meal-info">
              <nutrient-list [nutrients]="item.nutrients" [expandable]="false"
                [state]="useLinearNutrients ? 'linear' : 'inline-compact'" [displayMode]="displayMode"
                [useFullLabels]="useLinearNutrients"
              [match_pc]="getMealMatchPercentage(item.entry)"></nutrient-list>
            </div>
          }
          <!-- Special display for weigh-in items with icons -->
          @if (item.entry.type === LogRecordType.WeighIn) {
            <div class="weigh-in-info">
              <span class="weigh-in-item">
                <cs-icon name="monitor-weight"></cs-icon>
                <span>{{ item.weightValue }}</span>
              </span>
              <span class="weigh-in-item">
                <cs-icon name="straighten"></cs-icon>
                <span>{{ item.waistValue }}</span>
              </span>
              <span class="weigh-in-item">
                <cs-icon name="body-fat"></cs-icon>
                <span>{{ item.skinfoldsValue }}</span>
              </span>
            </div>
          }
          <!-- Warning message when overdue for weigh-in -->
          @if (atToday && item.entry.type === LogRecordType.WeighIn && showWeightWarning && !item.entry.weight_kg) {
            <cs-text-alert type="warning"
              >
              {{ weightWarningMessage }}
            </cs-text-alert>
          }
          <!-- Success message showing weight progress -->
          @if (atToday && item.entry.type === LogRecordType.WeighIn && showWeightProgress && item.entry.weight_kg) {
            <cs-text-alert [type]="isProgressIndeterminate ? 'info' : isOnTrackWithProgress ? 'success' : 'warning'"
              >
              {{ LL.dayItems.success.weightChange({
              amount: weightProgressAmount,
              unit: weightProgressUnit,
              direction: weightProgressDirection
              }) }}
              <a (click)="onViewProgressClick($event)">{{ LL.dayItems.success.viewProgress() }}</a>
            </cs-text-alert>
          }
          <!-- Undecided message: too few weigh-ins to state a weekly trend (#4306) -->
          @if (atToday && item.entry.type === LogRecordType.WeighIn && showWeightTrendUndecided && item.entry.weight_kg) {
            <cs-text-alert type="info">
              {{ LL.dayItems.info.notEnoughWeightData() }}
              <a (click)="onViewProgressClick($event)">{{ LL.dayItems.success.viewProgress() }}</a>
            </cs-text-alert>
          }
          <!-- Skipped workout message -->
          @if (item.entry.type === LogRecordType.Workout && item.entry.state === LogRecordState.Skipped && nextWorkoutDate) {
            <div class="skipped-workout-message"
              >
              <span>{{ LL.dayItems.skippedWorkout.message({ date: nextWorkoutDateFullLabel }) }} </span>
              @if (atToday) {
                <a (click)="handleRevertButton(item); $event.stopPropagation()">{{ LL.dayItems.skippedWorkout.moveToToday() }}</a>
              }
            </div>
          }
          <!-- Rest day info message -->
          @if (item.id === 'NoWorkout' && !item.entry._id && nextWorkout) {
            <div class="no-workout-message">
              @if (nextWorkoutDayName) {
                <span class="no-break">{{ LL.dayItems.noWorkout.nextWorkout({ day: nextWorkoutDayName }) }}</span>
              }
              @if (atToday) {
                <span class="no-break"> {{ LL.dayItems.noWorkout.message() }}</span>
              }
            </div>
          }
          @if (item.text && (userDb.nutritionTracking || item.entry.type !== LogRecordType.Meal) && item.entry.type !== LogRecordType.WeighIn && !(item.entry.type === LogRecordType.Workout && item.entry.state === LogRecordState.Skipped)) {
            <cs-markdown
            [textMarkdown]="item.text" slim></cs-markdown>
          }
          @if (item.entry.type === LogRecordType.Meal && (shouldShowMacrosMatchBadge(item.entry) || item.entry.autoPlanned)) {
            <div class="badges-container"
              >
              @if (shouldShowMacrosMatchBadge(item.entry)) {
                <cs-badge color="blue" icon="ribbon"
                  [label]="LL.dayItems.badges.macrosMatch()"
                [info]="LL.dayItems.badges.macrosMatchInfo()"></cs-badge>
              }
              @if (item.entry.autoPlanned) {
                <cs-badge color="purple" icon="calendar-meal"
                  [label]="LL.dayItems.badges.mealPlanner()"
                [info]="LL.dayItems.badges.mealPlannerInfo()"></cs-badge>
              }
            </div>
          }
        </cs-card-content>
      }
    </cs-card>
  }
</cs-card-list>
}